import PushPinIcon from '@mui/icons-material/PushPin';
import { DialogTitle, Grid } from '@mui/material';
import { useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import {
  IComposableDashboardCollection,
  IProfileDashboard,
  IComposableDashboardCollectionsContext,
} from 'wisestep-api';
import { IUserDTO, Item, WsButton, WsColors, WsDialog } from 'wisestep-base';

import { ConfirmDeleteDialog, IconForDialog } from '../../../components';
import { useUserContext } from '../../../context';
import ErrorLogger from '../../../providers/ErrorLogger';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import ConfirmationDialog from '../../clientDetails/components/ConfirmationDialog';
import AnalyticsContentLayout from '../components/AnalyticsContentLayout';
import ShareDashboardDialog from '../components/dashboard/ShareDashboardDialog';
import TagDashboardDialog from '../components/dashboard/TagDashboardDialog';
import SaveCollectionDialog from '../components/dashboardCollection/SaveCollectionDialog';
import { defaultPinnedDashboardCollection } from '../helper/getDefaultDashboardValues';
import useCollectionsOptions from '../hooks/useCollectionsOptions';
import { useComposableDashboardCollectionsContext } from '../hooks/useComposableDashboardCollectionsContext';
import useDeleteCollection from '../hooks/useDeleteCollection';
import useFetchAllProfileDashboards from '../hooks/useFetchAllProfileDashboards';
import useFetchTeamNames from '../hooks/useFetchTeamNames';
import useHandleAssignFlow from '../hooks/useHandleAssignFlow';
import usePinnedDashboardCollections from '../hooks/usePinnedDashboardCollections';
import usePromptText from '../hooks/usePromptText';
import useRenameDashboard from '../hooks/useRenameDashboard';
import useTagDashboardCollection from '../hooks/useTagDashboardCollection';

const DashboardCollectionsOptions = () => {
  const navigate = useNavigate();
  const [isAssignFlow, setIsAssignFlow] = useState<boolean>(false);
  const {
    pinnedDashboards,
    selectedPinnedDashboard,
    setSelectedPinnedDashboard,
    updateDashboardStatus,
    openShareDialog,
    setOpenShareDialog,
    handlePinningDashboard,
    openPinDialog,
    setOpenPinDialog,
  } = usePinnedDashboardCollections(isAssignFlow);
  const [selectedProfileDashboard, setSelectedProfileDashboard] = useState<IProfileDashboard>(
    defaultPinnedDashboardCollection
  );
  const { dashboardCollections, selectedDashboardCollection, setSelectedCollectionByIndex } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const { allowedTeams, fetchTeamNames } = useFetchTeamNames();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const { onRenameDashboard, openUpdateDialog, setOpenUpdateDialog } = useRenameDashboard();
  const { onDeleteCollection, setOpenDeleteDialog, openDeleteDialog } = useDeleteCollection();
  const { profileDashboards, setProfileDashboards } = useFetchAllProfileDashboards();
  const { handleTagDashboard, openTagDialog, setOpenTagDialog } = useTagDashboardCollection(
    profileDashboards,
    setProfileDashboards
  );

  const {
    openAssignToClientsDialog,
    openAssignToClientSpocDialog,
    openAssignToAccountManagerOUDialog,
    openAssignToTeamsOUDialog,
    openAssignToTeamsDialog,
    openAssignToAccountManagersDialog,
    openAssignToBusinessDefinitionDialog,
    openAssignToClientUnitDialog,
    openAssignToClientContactUnitDialog,
    openAssignToUsersDialog,
    openAssignToJobsDialog,
    setOpenAssignToClientsDialog,
    setOpenAssignToClientSpocDialog,
    setOpenAssignToAccountManagerOUDialog,
    setOpenAssignToTeamsOUDialog,
    setOpenAssignToTeamsDialog,
    setOpenAssignToAccountManagersDialog,
    setOpenAssignToBusinessDefinitionDialog,
    setOpenAssignToClientUnitDialog,
    setOpenAssignToClientContactUnitDialog,
    setOpenAssignToUsersDialog,
    setOpenAssignToJobsDialog,
    handleAssigningToClientsDashboard,
    handleAssigningToClientSpocDashboard,
    handleAssigningToAccountManagerOUDashboard,
    handleAssigningToTeamsOUDashboard,
    handleAssigningToTeamsDashboard,
    handleAssigningToAccountManagersDashboard,
    handleAssigningToBusinessDefinitionDashboard,
    handleAssigningToClientUnitDashboard,
    handleAssigningToClientContactUnitDashboard,
    handleAssigningToUsersDashboard,
    handleAssigningToJobsDashboard,
  } = useHandleAssignFlow();

  const handleOnClick = useCallback(
    async (
      index: number,
      action:
        | 'pin'
        | 'unpin'
        | 'assign'
        | 'share'
        | 'rename'
        | 'delete'
        | 'tag'
        | 'assignToClients'
        | 'unAssignToClients'
        | 'assignToClientSpoc'
        | 'unAssignToClientSpoc'
        | 'assignToAccountManagerOU'
        | 'unAssignToAccountManagerOU'
        | 'assignToTeamsOU'
        | 'unAssignToTeamsOU'
        | 'assignToTeams'
        | 'unAssignToTeams'
        | 'assignToAccountManagers'
        | 'unAssignToAccountManagers'
        | 'assignToBusinessUnit'
        | 'unAssignToBusinessUnit'
        | 'assignToClientUnit'
        | 'unAssignToClientUnit'
        | 'assignToClientContactUnit'
        | 'unAssignToClientContactUnit'
        | 'assignToUsers'
        | 'unAssignToUsers'
        | 'assignToJobs'
        | 'unAssignToJobs',
      creator?: boolean
    ) => {
      setSelectedCollectionByIndex(index);
      const dashboard = dashboardCollections?.[index];
      if (!dashboard) {
        setNotification(
          failed('Dashboard collection has not been selected properly. Please try again later!')
        );
        return;
      }
      const pinDashboard = pinnedDashboards?.find(
        (pd) => pd.dashboardCollection.id === dashboard?.id && pd.filters == null
      );
      setSelectedPinnedDashboard(
        pinDashboard?.id ? pinDashboard : defaultPinnedDashboardCollection
      );
      const taggedDashboard = profileDashboards?.find(
        ({ dashboardCollection }) => dashboardCollection.id === dashboard.id
      );
      if (taggedDashboard?.id) setSelectedProfileDashboard(taggedDashboard);

      switch (action) {
        case 'pin':
        case 'unpin':
          creator
            ? setOpenPinDialog(true)
            : setNotification(
                failed('Sorry, You cannot unpin dashboard collection as it is assigned to you !!')
              );
          break;
        case 'share': {
          const isTeamNamesFetched = await fetchTeamNames(action, dashboard.sharedWithTeams);
          if (isTeamNamesFetched) {
            setIsAssignFlow(false);
            setOpenShareDialog(true);
          }
          break;
        }
        case 'tag': {
          const isTeamNamesFetched = await fetchTeamNames(action, pinDashboard?.teams);
          if (isTeamNamesFetched) {
            setOpenTagDialog(true);
          }
          break;
        }
        case 'assign': {
          const isTeamNamesFetched = await fetchTeamNames(action, pinDashboard?.teams);
          if (isTeamNamesFetched) {
            setIsAssignFlow(true);
            setOpenShareDialog(true);
          }
          break;
        }
        case 'rename':
          setOpenUpdateDialog(true);
          break;
        case 'delete':
          setOpenDeleteDialog(true);
          break;
        case 'assignToClients':
          creator
            ? setOpenAssignToClientsDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToClients':
          creator
            ? setOpenAssignToClientsDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToClientSpoc':
          creator
            ? setOpenAssignToClientSpocDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToClientSpoc':
          creator
            ? setOpenAssignToClientSpocDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToAccountManagerOU':
          creator
            ? setOpenAssignToAccountManagerOUDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToAccountManagerOU':
          creator
            ? setOpenAssignToAccountManagerOUDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToTeamsOU':
          creator
            ? setOpenAssignToTeamsOUDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToTeamsOU':
          creator
            ? setOpenAssignToTeamsOUDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToTeams':
          creator
            ? setOpenAssignToTeamsDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToTeams':
          creator
            ? setOpenAssignToTeamsDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToAccountManagers':
          creator
            ? setOpenAssignToAccountManagersDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToAccountManagers':
          creator
            ? setOpenAssignToAccountManagersDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToBusinessUnit':
          creator
            ? setOpenAssignToBusinessDefinitionDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToBusinessUnit':
          creator
            ? setOpenAssignToBusinessDefinitionDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToClientUnit':
          creator
            ? setOpenAssignToClientUnitDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToClientUnit':
          creator
            ? setOpenAssignToClientUnitDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToClientContactUnit':
          creator
            ? setOpenAssignToClientContactUnitDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToClientContactUnit':
          creator
            ? setOpenAssignToClientContactUnitDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToUsers':
          creator
            ? setOpenAssignToUsersDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToUsers':
          creator
            ? setOpenAssignToUsersDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        case 'assignToJobs':
          creator
            ? setOpenAssignToJobsDialog(true)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'unAssignToJobs':
          creator
            ? setOpenAssignToJobsDialog(true)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
          break;
        default:
          break;
      }
    },
    [
      dashboardCollections,
      fetchTeamNames,
      pinnedDashboards,
      profileDashboards,
      setNotification,
      setOpenAssignToAccountManagerOUDialog,
      setOpenAssignToAccountManagersDialog,
      setOpenAssignToBusinessDefinitionDialog,
      setOpenAssignToClientContactUnitDialog,
      setOpenAssignToClientSpocDialog,
      setOpenAssignToClientUnitDialog,
      setOpenAssignToClientsDialog,
      setOpenAssignToJobsDialog,
      setOpenAssignToTeamsDialog,
      setOpenAssignToTeamsOUDialog,
      setOpenAssignToUsersDialog,
      setOpenDeleteDialog,
      setOpenPinDialog,
      setOpenShareDialog,
      setOpenTagDialog,
      setOpenUpdateDialog,
      setSelectedCollectionByIndex,
      setSelectedPinnedDashboard,
    ]
  );

  const { options, title } = useCollectionsOptions(handleOnClick, pinnedDashboards);

  const {
    pinPromptText,
    unPinPromptText,
    assignToClientsPromptText,
    unAssignToClientsPromptText,
    unAssignToClientSpocPromptText,
    assignToAccountManagerOUPromptText,
    unAssignToAccountManagerOUPromptText,
    assignToClientSpocPromptText,
    assignToTeamsOUPromptText,
    unAssignToTeamsOUPromptText,
    assignToTeamsPromptText,
    unAssignToTeamsPromptText,
    assignToAccountManagersPromptText,
    unAssignToAccountManagersPromptText,
    assignToBusinessDefinitionPromptText,
    unAssignToBusinessDefinitionPromptText,
    assignToClientUnitPromptText,
    unAssignToClientUnitPromptText,
    assignToClientContactUnitPromptText,
    unAssignToClientContactUnitPromptText,
    assignToUsersPromptText,
    unAssignToUsersPromptText,
    assignToJobsPromptText,
    unAssignToJobsPromptText,
  } = usePromptText();

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Grid item xs={12}>
        <Item sx={{ backgroundColor: WsColors.White, height: '100%' }}>
          <AnalyticsContentLayout
            options={options ?? []}
            title={title}
            headerButton={
              <WsButton
                onClick={() => {
                  setSelectedCollectionByIndex(-1);
                  navigate('/workspace-users-and-teams/master-dashboards/view');
                }}
              >
                Create new Dashboard Collection
              </WsButton>
            }
            setSelectedDashboardId="master"
          />
          {openPinDialog && (
            <WsDialog openDialog={openPinDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedPinnedDashboard?.id &&
                  loggedInUser?.email &&
                  selectedPinnedDashboard.users?.includes(loggedInUser.email)
                    ? unPinPromptText
                    : pinPromptText
                }`}
                onConfirm={() =>
                  handlePinningDashboard(selectedDashboardCollection?.id ?? '', null)
                }
                onSkip={() => {
                  setSelectedPinnedDashboard(defaultPinnedDashboardCollection);
                  setOpenPinDialog(false);
                }}
                confirmText={`${
                  selectedPinnedDashboard?.id &&
                  loggedInUser?.email &&
                  selectedPinnedDashboard.users?.includes(loggedInUser.email)
                    ? 'Unpin'
                    : 'Pin'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToClientsDialog && (
            <WsDialog openDialog={openAssignToClientsDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToClients
                    ? unAssignToClientsPromptText
                    : assignToClientsPromptText
                }`}
                onConfirm={() => handleAssigningToClientsDashboard()}
                onSkip={() => {
                  setOpenAssignToClientsDialog(false);
                }}
                confirmText={`${
                  selectedDashboardCollection.assignToClients ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToClientSpocDialog && (
            <WsDialog openDialog={openAssignToClientSpocDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToClientSpoc
                    ? unAssignToClientSpocPromptText
                    : assignToClientSpocPromptText
                }`}
                onConfirm={() => handleAssigningToClientSpocDashboard()}
                onSkip={() => {
                  setOpenAssignToClientSpocDialog(false);
                }}
                confirmText={`${
                  selectedDashboardCollection.assignToClientSpoc ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToAccountManagerOUDialog && (
            <WsDialog openDialog={openAssignToAccountManagerOUDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToAccountManagerOU
                    ? unAssignToAccountManagerOUPromptText
                    : assignToAccountManagerOUPromptText
                }`}
                onConfirm={() => handleAssigningToAccountManagerOUDashboard()}
                onSkip={() => {
                  setOpenAssignToAccountManagerOUDialog(false);
                }}
                confirmText={`${
                  selectedDashboardCollection.assignToAccountManagerOU ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToTeamsOUDialog && (
            <WsDialog openDialog={openAssignToTeamsOUDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToTeamsOU
                    ? unAssignToTeamsOUPromptText
                    : assignToTeamsOUPromptText
                }`}
                onConfirm={() => handleAssigningToTeamsOUDashboard()}
                onSkip={() => {
                  setOpenAssignToTeamsOUDialog(false);
                }}
                confirmText={`${
                  selectedDashboardCollection.assignToTeamsOU ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToTeamsDialog && (
            <WsDialog openDialog={openAssignToTeamsDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToTeams
                    ? unAssignToTeamsPromptText
                    : assignToTeamsPromptText
                }`}
                onConfirm={() => handleAssigningToTeamsDashboard()}
                onSkip={() => {
                  setOpenAssignToTeamsDialog(false);
                }}
                confirmText={`${selectedDashboardCollection.assignToTeams ? 'Unassign' : 'Assign'}`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToAccountManagersDialog && (
            <WsDialog openDialog={openAssignToAccountManagersDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToAccountManagers
                    ? unAssignToAccountManagersPromptText
                    : assignToAccountManagersPromptText
                }`}
                onConfirm={() => handleAssigningToAccountManagersDashboard()}
                onSkip={() => {
                  setOpenAssignToAccountManagersDialog(false);
                }}
                confirmText={`${
                  selectedDashboardCollection.assignToAccountManagers ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToBusinessDefinitionDialog && (
            <WsDialog openDialog={openAssignToBusinessDefinitionDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToBusinessUnit
                    ? unAssignToBusinessDefinitionPromptText
                    : assignToBusinessDefinitionPromptText
                }`}
                onConfirm={() => handleAssigningToBusinessDefinitionDashboard()}
                onSkip={() => {
                  setOpenAssignToBusinessDefinitionDialog(false);
                }}
                confirmText={`${
                  selectedDashboardCollection.assignToBusinessUnit ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToClientUnitDialog && (
            <WsDialog openDialog={openAssignToClientUnitDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToClientUnit
                    ? unAssignToClientUnitPromptText
                    : assignToClientUnitPromptText
                }`}
                onConfirm={() => handleAssigningToClientUnitDashboard()}
                onSkip={() => setOpenAssignToClientUnitDialog(false)}
                confirmText={`${
                  selectedDashboardCollection.assignToClientUnit ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToClientContactUnitDialog && (
            <WsDialog openDialog={openAssignToClientContactUnitDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToClientContactUnit
                    ? unAssignToClientContactUnitPromptText
                    : assignToClientContactUnitPromptText
                }`}
                onConfirm={() => handleAssigningToClientContactUnitDashboard()}
                onSkip={() => setOpenAssignToClientContactUnitDialog(false)}
                confirmText={`${
                  selectedDashboardCollection.assignToClientContactUnit ? 'Unassign' : 'Assign'
                }`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToUsersDialog && (
            <WsDialog openDialog={openAssignToUsersDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToUsers
                    ? unAssignToUsersPromptText
                    : assignToUsersPromptText
                }`}
                onConfirm={() => handleAssigningToUsersDashboard()}
                onSkip={() => setOpenAssignToUsersDialog(false)}
                confirmText={`${selectedDashboardCollection.assignToUsers ? 'Unassign' : 'Assign'}`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openAssignToJobsDialog && (
            <WsDialog openDialog={openAssignToJobsDialog}>
              <DialogTitle>
                <IconForDialog icon={<PushPinIcon />} />
              </DialogTitle>
              <ConfirmationDialog
                prompt={`${
                  selectedDashboardCollection.assignToJobs
                    ? unAssignToJobsPromptText
                    : assignToJobsPromptText
                }`}
                onConfirm={() => handleAssigningToJobsDashboard()}
                onSkip={() => setOpenAssignToJobsDialog(false)}
                confirmText={`${selectedDashboardCollection.assignToJobs ? 'Unassign' : 'Assign'}`}
                skipText="Cancel"
              />
            </WsDialog>
          )}
          {openShareDialog && (
            <ShareDashboardDialog
              dashboard={selectedDashboardCollection}
              onCloseDialog={() => setOpenShareDialog(false)}
              openShareDialog={openShareDialog}
              onSave={(dashboard) =>
                updateDashboardStatus(dashboard as IComposableDashboardCollection)
              }
              isAssignFlow={isAssignFlow}
              sharedToTeams={allowedTeams}
              selectedPinnedDashboard={selectedPinnedDashboard}
            />
          )}
          {openDeleteDialog && (
            <ConfirmDeleteDialog
              open={openDeleteDialog}
              onClose={() => setOpenDeleteDialog(false)}
              onConfirm={onDeleteCollection}
              deletingItem={`${selectedDashboardCollection.name}`}
            />
          )}
          {openUpdateDialog && (
            <SaveCollectionDialog
              openSaveModal={openUpdateDialog}
              setSaveModal={setOpenUpdateDialog}
              saveCollection={onRenameDashboard}
              update={true}
              customUpdateText="Update Dashboard Collection Name"
            />
          )}
          {openTagDialog && (
            <TagDashboardDialog
              dashboardCollection={selectedDashboardCollection}
              onCloseDialog={() => setOpenTagDialog(false)}
              onSave={handleTagDashboard}
              sharedToTeams={allowedTeams}
              selectedProfileDashboard={selectedProfileDashboard}
            />
          )}
        </Item>
      </Grid>
    </ErrorBoundary>
  );
};

export default DashboardCollectionsOptions;
